import type {
  ReadTableOrderStoreRequest,
  ReadTableOrderStoreResponse,
  WriteTableOrderStoreRequest,
  WriteTableOrderStoreResponse,
  DeleteTableOrderStoreRequest,
  DeleteTableOrderStoreResponse,
  CheckPasswordRequest,
  CheckPasswordResponse,
  TableOrderSaveInvoiceRequest,
  TableOrderSaveInvoiceResponse,
  OpenBottleRequest,
  OpenBottleResponse,
  ValidateTableOrderStockRequest,
  ValidateTableOrderStockResponse,
  WriteTableOrderLogRequest,
  WriteTableOrderLogResponse,
  BroadcastTableOrderLogRequest,
  FetchTableOrderAuditRequest,
  FetchTableOrderAuditResponse,
  GetUpdatePriceMaterialResponse,
  UpdateNewMrpRequest,
  UpdateNewMrpResponse,
  UpdateStockRequest,
  UpdateStockResponse,
} from "../types/table-order";
import { getAuthHeaders, handleAuthError } from "./auth-api";
import { getRealtimeSocket } from "../realtime/socket";
import { formatApiError } from "./error-message";
import { adaptReadTableOrderStoreData } from "../lib/table-order-adapters";

let baseUrl = "";
const API_PREFIX = "/api/v1";

export function setTableOrderBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, "");
}

function getSocketIdHeader(): Record<string, string> {
  const id = getRealtimeSocket()?.id;
  return id ? { "X-Socket-Id": id } : {};
}

async function postTableOrder<T>(
  endpoint: string,
  data: Record<string, unknown>,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...(extraHeaders ?? {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

async function getTableOrder<T>(endpoint: string): Promise<T> {
  const url = `${baseUrl}${API_PREFIX}${endpoint}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      handleAuthError();
    }
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }

  return response.json() as Promise<T>;
}

export async function readTableOrderStore(
  request: ReadTableOrderStoreRequest,
): Promise<ReadTableOrderStoreResponse> {
  const res = await postTableOrder<ReadTableOrderStoreResponse>(
    "/table-order/read",
    request as unknown as Record<string, unknown>,
  );
  // Normalize legacy-shaped drafts (old wobpos / un-updated waiter app)
  // to the canonical TableOrderItem keys — see table-order-adapters.ts.
  return { ...res, data: adaptReadTableOrderStoreData(res.data) };
}

export async function writeTableOrderStore(
  request: WriteTableOrderStoreRequest,
): Promise<WriteTableOrderStoreResponse> {
  const res = await postTableOrder<WriteTableOrderStoreResponse>(
    "/table-order/write",
    request as unknown as Record<string, unknown>,
    getSocketIdHeader(),
  );
  // The server echoes the table's full state, which may include
  // legacy-shaped items written by old wobpos / un-updated waiter app
  // clients. Normalize so the cache always holds English-keyed items.
  if (res?.data?.tableData) {
    res.data.tableData = adaptReadTableOrderStoreData(res.data.tableData);
  }
  return res;
}

export async function deleteTableOrderStore(
  request: DeleteTableOrderStoreRequest,
): Promise<DeleteTableOrderStoreResponse> {
  const res = await postTableOrder<DeleteTableOrderStoreResponse>(
    "/table-order/delete",
    request as unknown as Record<string, unknown>,
    getSocketIdHeader(),
  );
  if (res?.data?.tableData) {
    res.data.tableData = adaptReadTableOrderStoreData(res.data.tableData);
  }
  return res;
}

export async function checkTableOrderPassword(
  request: CheckPasswordRequest,
): Promise<CheckPasswordResponse> {
  return postTableOrder<CheckPasswordResponse>(
    "/table-order/check-password",
    request as unknown as Record<string, unknown>,
  );
}

export async function saveTableOrderInvoice(
  request: TableOrderSaveInvoiceRequest,
): Promise<TableOrderSaveInvoiceResponse> {
  return postTableOrder<TableOrderSaveInvoiceResponse>(
    "/table-order/save-invoice",
    request as unknown as Record<string, unknown>,
  );
}

export async function openBottle(
  request: OpenBottleRequest,
): Promise<OpenBottleResponse> {
  return postTableOrder<OpenBottleResponse>(
    "/table-order/open-bottle",
    request as unknown as Record<string, unknown>,
  );
}

export async function validateTableOrderStock(
  request: ValidateTableOrderStockRequest,
): Promise<ValidateTableOrderStockResponse> {
  return postTableOrder<ValidateTableOrderStockResponse>(
    "/table-order/validate-stock",
    request as unknown as Record<string, unknown>,
  );
}

export function writeTableOrderLog(
  request: WriteTableOrderLogRequest,
): Promise<WriteTableOrderLogResponse> {
  return postTableOrder<WriteTableOrderLogResponse>(
    "/table-order/log",
    request as unknown as Record<string, unknown>,
  );
}

// Fire-and-forget broadcast to other clients in the shop room. Server
// excludes the sender (identified by the X-Socket-Id header), so this
// client should enqueue its own copy to the local buffer separately.
export function broadcastTableOrderLog(
  request: BroadcastTableOrderLogRequest,
): Promise<{ message: string }> {
  return postTableOrder<{ message: string }>(
    "/table-order/log/broadcast",
    request as unknown as Record<string, unknown>,
    getSocketIdHeader(),
  );
}

export function writeTableOrderLogBatch(
  docid: string,
  events: BroadcastTableOrderLogRequest[],
): Promise<{ message: string }> {
  return postTableOrder<{ message: string }>("/table-order/log/batch", {
    docid,
    events,
  });
}

export async function fetchTableOrderAudit(
  params: FetchTableOrderAuditRequest = {},
): Promise<FetchTableOrderAuditResponse> {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") {
      search.set(k, String(v));
    }
  }
  const qs = search.toString();
  const url = `${baseUrl}${API_PREFIX}/table-order/audit${qs ? `?${qs}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { ...getAuthHeaders() },
  });
  if (!response.ok) {
    if (response.status === 401) handleAuthError();
    const errorBody = await response.json().catch(() => null);
    throw new Error(
      formatApiError(errorBody, response.status, response.statusText),
    );
  }
  return response.json() as Promise<FetchTableOrderAuditResponse>;
}

export async function getUpdatePriceMaterial(): Promise<GetUpdatePriceMaterialResponse> {
  return getTableOrder<GetUpdatePriceMaterialResponse>(
    "/table-order/food-materials",
  );
}

export async function updateNewMrp(
  request: UpdateNewMrpRequest,
): Promise<UpdateNewMrpResponse> {
  return postTableOrder<UpdateNewMrpResponse>(
    "/table-order/update-mrp",
    request as unknown as Record<string, unknown>,
  );
}

export async function updateStock(
  request: UpdateStockRequest,
): Promise<UpdateStockResponse> {
  return postTableOrder<UpdateStockResponse>(
    "/table-order/update-stock",
    request as unknown as Record<string, unknown>,
  );
}
